import { Component, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { captureSentryException, showSentryReportDialog, withSentry } from '@/lib/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  eventId?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Error caught by boundary:', error, errorInfo);

    withSentry((Sentry) => {
      Sentry.withScope((scope) => {
        scope.setContext('errorBoundary', {
          componentStack: errorInfo.componentStack,
        });
        const eventId = Sentry.captureException(error);
        this.setState({ eventId });
      });
    });

    captureSentryException(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center px-4">
          <div className="w-full max-w-2xl space-y-8 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-destructive/10 px-4 py-2 text-destructive text-sm font-medium ring-1 ring-destructive/20">
              <AlertCircle className="h-4 w-4" />
              <span>Unexpected error (500)</span>
            </div>
            <div className="space-y-3">
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">We hit a snag</h1>
              <p className="text-muted-foreground text-base sm:text-lg">
                Something broke while loading your page. You can retry, jump back to the dashboard, or tell us what happened.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 max-w-2xl mx-auto">
              <Button onClick={() => window.location.reload()} className="w-full">
                Refresh
              </Button>
              <Button asChild variant="outline" className="w-full">
                <a href="/dashboard">Go to dashboard</a>
              </Button>
              {this.state.eventId ? (
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => showSentryReportDialog({ eventId: this.state.eventId })}
                >
                  Report this
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    showSentryReportDialog({});
                  }}
                >
                  Report this
                </Button>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              Need help? <a className="text-primary hover:underline" href="mailto:support@utmguard.com">Contact support</a>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <details className="text-left mt-4 p-4 bg-muted rounded-lg">
                <summary className="cursor-pointer font-semibold">Error details (dev only)</summary>
                <pre className="mt-2 text-xs overflow-auto whitespace-pre-wrap">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
